import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, X, CheckCircle2, Clock, Calendar, CheckSquare, Pencil, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EditTaskModal = ({ onClose, onEdit, task, employees }) => {
  const [formData, setFormData] = useState(task);
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-md relative shadow-2xl">
        <button onClick={onClose} className="absolute top-6 right-6 text-text_secondary hover:text-text_primary transition-colors"><X size={20}/></button>
        <h2 className="text-2xl font-serif text-text_primary mb-6">Update Task</h2>
        <form onSubmit={(e) => { e.preventDefault(); onEdit(formData); }} className="space-y-5">
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Task Title</label>
            <input required type="text" value={formData.title} onChange={e=>setFormData({...formData, title: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
          </div>
          <div>
            <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Description</label>
            <textarea required rows="3" value={formData.description} onChange={e=>setFormData({...formData, description: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50"></textarea>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Assign To</label>
              <select required value={formData.assigned_to} onChange={e=>setFormData({...formData, assigned_to: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                <option value="">-- Employee --</option>
                {employees.map(emp => <option key={emp.id} value={emp.name}>{emp.name} ({emp.role})</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Due Date</label>
              <input required type="date" value={formData.due_date} onChange={e=>setFormData({...formData, due_date: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
            </div>
          </div>
          <button type="submit" className="w-full bg-primary text-[#111827] font-bold py-4 rounded-xl mt-8 hover:bg-primary_hover transition-colors shadow-md">Save Changes</button>
        </form>
      </div>
    </div>
  );
};

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [formData, setFormData] = useState({ title: "", description: "", assigned_to: "", due_date: "" });

  const fetchData = useCallback(async () => {
    try {
      const resTasks = await axios.get(`${BACKEND_URL}/api/tasks`);
      setTasks(resTasks.data);
      const resHR = await axios.get(`${BACKEND_URL}/api/hr`);
      setEmployees(resHR.data);
    } catch(e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/tasks`, formData);
      setShowModal(false);
      setFormData({ title: "", description: "", assigned_to: "", due_date: "" });
      fetchData();
    } catch(e) { console.error(e); }
  };

  const handleEdit = async (data) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/update/tasks/${data.id}`, data);
      setShowEdit(null);
      fetchData();
    } catch(e) { alert(e.response?.data?.detail || "Admin access required"); }
  };

  const handleDelete = async (id) => {
    if(window.confirm("Delete this task permanently?")) {
      try {
        await axios.delete(`${BACKEND_URL}/api/admin/delete/tasks/${id}`);
        fetchData();
      } catch(e) { alert(e.response?.data?.detail || "Admin access required"); }
    }
  };

  const markComplete = async (taskId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/tasks/${taskId}/complete`);
      fetchData();
    } catch(e) { console.error(e); }
  };

  const pendingTasks = tasks.filter(t => t.status === "Pending");
  const completedTasks = tasks.filter(t => t.status === "Completed");

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700 relative">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-6 mb-4">
        <div>
          <h1 className="text-4xl font-serif text-text_primary mb-3">Tasks</h1>
          <p className="text-text_secondary text-sm">Assign, track, and complete daily operational workflows.</p>
        </div>
        <button onClick={() => setShowModal(true)} className="bg-primary text-[#111827] px-6 py-3.5 rounded-xl font-bold hover:bg-primary_hover transition-colors flex items-center gap-2 shadow-md">
          <Plus size={20} /> Assign Task
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-card_bg border border-border_soft/40 rounded-2xl p-8 shadow-sm">
          <h3 className="text-xl font-serif text-text_primary mb-6 flex items-center gap-3"><Clock size={20} className="text-gold"/> Pending Operations</h3>
          <div className="space-y-4">
            {pendingTasks.length === 0 && <p className="text-text_secondary text-sm italic">All caught up! No pending tasks.</p>}
            {pendingTasks.map(t => (
              <div key={t.id} className="bg-card_bg/60 p-5 rounded-2xl border border-border_soft/20 hover:border-primary/40 transition-colors relative group">
                <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity bg-card_bg p-1 rounded-lg border border-border_soft/30">
                  <button onClick={() => setShowEdit(t)} className="p-1.5 text-text_secondary hover:text-gold transition-colors" title="Edit"><Pencil size={14} /></button>
                  <button onClick={() => handleDelete(t.id)} className="p-1.5 text-text_secondary hover:text-accent_danger transition-colors" title="Delete"><Trash2 size={14} /></button>
                </div>
                <div className="flex justify-between items-start mb-3 pr-16">
                  <h4 className="text-text_primary font-bold text-lg leading-tight">{t.title}</h4>
                </div>
                <p className="text-text_secondary text-sm mb-5 leading-relaxed">{t.description}</p>
                <div className="flex justify-between items-center text-xs border-t border-border_soft/20 pt-4">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-primary/20 text-gold flex items-center justify-center font-bold">{t.assigned_to.charAt(0)}</span>
                    <span className="text-text_primary font-medium">{t.assigned_to}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-accent_danger flex items-center gap-1.5 font-medium bg-accent_danger/10 px-2 py-1 rounded-md"><Calendar size={12}/> {t.due_date}</span>
                    <button onClick={() => markComplete(t.id)} className="text-gold hover:text-text_primary hover:scale-110 transition-all bg-primary/10 p-1.5 rounded-full" title="Mark as Completed">
                      <CheckCircle2 size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-main_bg border border-border_soft/20 rounded-2xl p-8 shadow-inner opacity-80">
          <h3 className="text-xl font-serif text-text_secondary mb-6 flex items-center gap-3"><CheckSquare size={20} className="text-accent_success"/> Completed Log</h3>
          <div className="space-y-4">
            {completedTasks.length === 0 && <p className="text-text_secondary/50 text-sm italic">Archive is empty.</p>}
            {completedTasks.map(t => (
              <div key={t.id} className="bg-card_bg/30 p-5 rounded-2xl border border-accent_success/10 relative group">
                <button onClick={() => handleDelete(t.id)} className="absolute top-4 right-4 text-text_secondary hover:text-accent_danger transition-colors opacity-0 group-hover:opacity-100 p-1.5 bg-card_bg rounded-lg" title="Delete Permanently"><Trash2 size={14} /></button>
                <h4 className="text-text_secondary font-bold line-through mb-3 pr-8">{t.title}</h4>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-text_secondary/70">{t.assigned_to}</span>
                  <span className="text-accent_success flex items-center gap-1.5 font-bold tracking-wider uppercase"><CheckCircle2 size={14}/> Finished</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showEdit && <EditTaskModal task={showEdit} employees={employees} onClose={() => setShowEdit(null)} onEdit={handleEdit} />}

      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-card_bg border border-border_soft p-8 rounded-2xl w-full max-w-md relative shadow-2xl">
            <button onClick={() => setShowModal(false)} className="absolute top-6 right-6 text-text_secondary hover:text-text_primary transition-colors"><X size={20}/></button>
            <h2 className="text-2xl font-serif text-text_primary mb-6">Add Task</h2>
            <form onSubmit={handleCreateTask} className="space-y-5">
              <div>
                <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Task Title</label>
                <input required type="text" value={formData.title} onChange={e=>setFormData({...formData, title: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
              </div>
              <div>
                <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Description</label>
                <textarea required rows="3" value={formData.description} onChange={e=>setFormData({...formData, description: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50"></textarea>
              </div>
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Assign To</label>
                  <select required value={formData.assigned_to} onChange={e=>setFormData({...formData, assigned_to: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary appearance-none focus:ring-1 focus:ring-primary/50">
                    <option value="">-- Employee --</option>
                    {employees.map(emp => <option key={emp.id} value={emp.name}>{emp.name} ({emp.role})</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider">Due Date</label>
                  <input required type="date" value={formData.due_date} onChange={e=>setFormData({...formData, due_date: e.target.value})} className="w-full bg-card_bg border border-border_soft rounded-xl p-3.5 text-text_primary focus:ring-1 focus:ring-primary/50" />
                </div>
              </div>
              <button type="submit" className="w-full bg-primary text-[#111827] font-bold py-4 rounded-xl mt-8 hover:bg-primary_hover transition-colors shadow-md">Add Task</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
